import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import UserForm from "./UserForm";
import UserList from "./UserList";
import HeroSectionV2 from "../../../components/HeroSectionV2";
import Billede1 from "../../../Images/Billede1.jpg";
import { User, Role, EventDto } from "./types";
import Modal from "../../../components/Modal/Modal";
import './UserAdmin.css';
import "../../../Layouts/buttons.css";
import "../../../Layouts/table.css";

const UserAdmin: React.FC = () => {
    const apiUrl = process.env.REACT_APP_API_URL as string;

    const [users, setUsers] = useState<User[]>([]);
    const [roles, setRoles] = useState<Role[]>([]);
    const [events, setEvents] = useState<EventDto[]>([]);
    const [selectedUser, setSelectedUser] = useState<User | null>(null);
    const [page, setPage] = useState(1);
    const [totalUsers, setTotalUsers] = useState(0);
    const [search, setSearch] = useState("");

    const pageSize = 50;

    const fetchJsonSafe = async (url: string, options?: RequestInit) => {
        try {
            const res = await fetch(url, options);
            if (!res.ok) throw new Error(await res.text());
            return res.headers.get("content-type")?.includes("application/json") ? await res.json() : null;
        } catch (err) {
            console.error("Fetch error:", err);
            return null;
        }
    };

    const fetchUsers = async () => {
        const data = await fetchJsonSafe(`${apiUrl}/api/users?page=${page}&pageSize=${pageSize}&search=${encodeURIComponent(search)}`);
        if (data) {
            setUsers(data.users);
            setTotalUsers(data.totalUsers);
        }
    };

    const fetchRoles = async () => {
        const data = await fetchJsonSafe(`${apiUrl}/api/users/roles`);
        if (data) setRoles(data);
    };

    const fetchEvents = async () => {
        const data = await fetchJsonSafe(`${apiUrl}/api/events`);
        if (data) {
            setEvents(data.map((e: any) => ({
                eventId: e.eventID,
                eventName: e.eventName,
                active: e.active,
                startTime: e.startTime,
                endTime: e.endTime
            })));
        }
    };

    useEffect(() => {
        fetchUsers();
        fetchRoles();
        fetchEvents();
    }, [page, search]);

    const handleEditUser = async (user: User) => {
        const roleIds = user.userRoles?.map(r => r.roleId) || [];

        const res = await fetch(`${apiUrl}/api/users/${user.userId}/events`);
        const userEvents: { eventId: number }[] = res.ok ? await res.json() : [];
        const eventIds = userEvents.map(e => Number(e.eventId)).filter(id => !isNaN(id));

        setSelectedUser({ ...user, roleIds, eventIds });
    };

    const handleSaveUser = async (user: User) => {
        // Update user roles
        await fetchJsonSafe(`${apiUrl}/api/users/${user.userId}`, {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                UserId: user.userId,
                Email: user.email,
                FirstName: user.firstName,
                LastName: user.lastName,
                Phone: user.phone,
                RoleIds: user.roleIds || []
            })
        });

        // Update user events
        const currentRes = await fetch(`${apiUrl}/api/users/${user.userId}/events`);
        // Fetch current events for user
        const currentIds = (currentRes.ok ? await currentRes.json() : [])
            .map((e: any) => Number(e.eventId))
            .filter((id: number) => !isNaN(id));  // <-- add :number here

        const newIds = (user.eventIds || []).map(Number).filter((id: number) => !isNaN(id)); // same here

        // Add new links
        await Promise.all(
            newIds
                .filter((id: number) => !currentIds.includes(id))
                .map((id: number) =>
                    fetch(`${apiUrl}/api/users/${user.userId}/events/${id}`, { method: "POST" })
                )
        );

        // Remove old links
        await Promise.all(
            currentIds
                .filter((id: number) => !newIds.includes(id))
                .map((id: number) =>
                    fetch(`${apiUrl}/api/users/${user.userId}/events/${id}`, { method: "DELETE" })
                )
        );

        await fetchUsers();
        setSelectedUser(null);
    };

    const handleDeleteUser = async (userId: number) => {
        if (!window.confirm("Slet denne bruger?")) return;
        await fetchJsonSafe(`${apiUrl}/api/users/${userId}`, { method: "DELETE" });
        await fetchUsers();
    };

    const navigate = useNavigate();

    const handleLoginAsUser = async (user: User) => {
        const adminToken = sessionStorage.getItem("authToken");
        if (!adminToken) return alert("Du skal være logget ind som admin først!");
        if (!window.confirm(`Vil du logge ind som ${user.email}?`)) return;

        try {
            const res = await fetch(`${apiUrl}/api/users/impersonate/${user.userId}`, {
                method: "POST",
                headers: { "Authorization": `Bearer ${adminToken}` }
            });
            if (!res.ok) return alert("Fejl ved login som bruger: " + await res.text());

            const data = await res.json();

            ["authToken", "userId", "personId", "username", "userEmail", "avatarUrl", "phone", "firstName", "requireProfileUpdate", "userRoles"]
                .forEach(k => sessionStorage.removeItem(k));

            sessionStorage.setItem("authToken", data.token);
            sessionStorage.setItem("userId", String(data.userId));
            sessionStorage.setItem("personId", String(data.personId));
            sessionStorage.setItem("username", `${data.firstName} ${data.lastName}`);
            sessionStorage.setItem("userEmail", data.email || "");
            sessionStorage.setItem("firstName", data.firstName || "");
            sessionStorage.setItem("avatarUrl", "");
            sessionStorage.setItem("phone", "");
            sessionStorage.setItem("requireProfileUpdate", "0");
            sessionStorage.setItem("userRoles", JSON.stringify(user.userRoles?.map(r => r.roleName) || []));

            window.dispatchEvent(new Event("auth-changed"));
            navigate("/UserDashboard");
        } catch (err) {
            console.error("Impersonation failed", err);
            alert("Fejl ved login som bruger.");
        }
    };

    return (
        <div>
            <HeroSectionV2
                titleEn="User Admin"
                titleDa="Bruger Admin"
                descriptionEn="Manage events, groups and functions"
                descriptionDa="Administrér events, grupper og funktioner"
                imageSrc={Billede1}
            />
            <div className="user-admin-page">
                <div className="search-panel">
                    <div className="search-container">
                        <input
                            type="text"
                            placeholder="Søg efter brugere..."
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                        />
                            <button onClick={() => { setPage(1); fetchUsers(); }} className="btn btn--new">Søg</button>
                    </div>
                </div>

                <Modal
                    isOpen={!!selectedUser}
                    title="Redigér bruger"
                    onClose={() => setSelectedUser(null)}
                >
                    {selectedUser && (
                        <UserForm
                            user={selectedUser}
                            roles={roles}
                            events={events}
                            onSave={handleSaveUser}
                            onCancel={() => setSelectedUser(null)}
                        />
                    )}
                </Modal>

            <UserList
                users={users}
                onEdit={handleEditUser}
                onDelete={handleDeleteUser}
                onLoginAs={handleLoginAsUser}
            />

            <div className="pagination">
                <span>Side {page} af {Math.ceil(totalUsers / pageSize)}</span>
                <div>
                        <button onClick={() => setPage(p => Math.max(1, p - 1))} className="btn btn--function" disabled={page === 1}>Forrige</button>
                        <button onClick={() => setPage(p => Math.min(Math.ceil(totalUsers / pageSize), p + 1))} className="btn btn--default" disabled={page >= Math.ceil(totalUsers / pageSize)}>Næste</button>
                </div>
            </div>
            </div>
        </div>
    );
};

export default UserAdmin;